import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Kingdom Intelligence: Planning & Archives
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      The strength of your kingdom lies not just in its armies, but in its
      coordination and strategy. Success depends on how well you and your allies
      communicate and share intelligence.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      To aid in this, the Lords of Mythea have provided two essential tools:
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>
        <strong>Kingdom Forums</strong> – A space for discussion, planning, and
        coordination.
      </li>
      <li>
        <strong>Kingdom Archives</strong> – A shared intelligence repository
        where crucial information about enemy and allied provinces is stored.
      </li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      Every time you Crystal Ball, Spy on Military, or conduct any other
      intelligence operation, the information gathered is automatically stored
      in your kingdom&apos;s archives. This allows your team to make
      well-informed decisions—whether preparing for war, launching an attack, or
      planning covert operations.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      However, knowing your enemies is just as important as knowing yourselves.
      Your kingdom archives can also store information about your own province,
      making it easier for your allies to coordinate with you.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      <strong>Note:</strong> Only members of your own kingdom can access the
      archives. Always check the timestamp on intelligence reports—outdated
      information can lead to disastrous decisions.
    </p>
  </div>
);

const StoringIntelSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Storing Intelligence
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      Your own province&apos;s status is automatically stored in the Kingdom
      Archives whenever you visit the Throne Room (Overview).
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Enemy intelligence is automatically recorded whenever you perform an
      intelligence-gathering operation against a province.
    </p>
  </div>
);

const AccessingIntelSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Accessing Intelligence
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      To review stored information on any province, simply navigate to the
      Kingdom Page. From there, you can access the latest reports and use them
      to plan your next move.
    </p>
  </div>
);

const FinalThoughtsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Final Thoughts</h3>
    <p className="text-[#b8b8b8] mb-2">
      In Mythea, knowledge is power. A well-coordinated kingdom that shares
      intelligence effectively will always have the upper hand. Use the Kingdom
      Forums and Archives wisely, and you will turn information into victory.
    </p>
  </div>
);

export const Route = createFileRoute('/kingdom-intelligence/')({
  component: KingdomIntelligencePage,
});

function KingdomIntelligencePage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/kingdom-intelligence" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/intel@2x.png"
              alt="Kingdom Intelligence"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Kingdom Intelligence</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <StoringIntelSection />
            <AccessingIntelSection />
            <FinalThoughtsSection />
          </div>
        </section>
      </div>
    </div>
  );
}
